import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import SkeletonGrid from '../../../../components/SkeletonLoader/SkeletonGrid';
import DropDownSelect from '../../../../components/DropDownSelect/index';
import { useNavigate } from 'react-router';
import CustomToast from '../../../../components/Toast';
import { useGetClassesMutation, useGetAllCourseListMutation } from '../../../../features/authentication/authApiSlice';
import DropDownMultiSelect from '../../../../components/DropDownMultiSelect';
import { useLazyGetMultipleClassStudentsQuery } from '../../../../features/authentication/classesApiSlice';

const SelfStudyFilter = () => {
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [filterBy, setFilterBy] = useState(0);
  const [studentName, setStudentName] = useState('');
  const takeSize = 30;

  const navigate = useNavigate();

  const [getCourses, { isLoading: isCourseLoading }] = useGetAllCourseListMutation();
  const [getClasses, { isLoading: isClassLoading }] = useGetClassesMutation();
  const [getStudents, { isLoading: isStudentLoading, isFetching: isStudentFetching }] = useLazyGetMultipleClassStudentsQuery();

  useEffect(() => {
    getReportFilterData();
  }, []);

  const getAllCoursesAsync = async () => {
    var courses = localStorage.getItem('courses');
    if (courses){
      courses = JSON.parse(courses);
    }
    else{
      courses = await getCourses().unwrap();
      localStorage.setItem('courses', JSON.stringify(courses));
    }
    const obj = { id: -1, name: 'All Courses' };
    courses = [obj, ...courses.filter(c => c.id !== 0).map(s => ({ id: s.id, name: s.name }))];
    setCourses(courses);
    setSelectedCourseIds(courses.map(x => x.id));
  };

  async function loadClasses() {
    var classes = await getClasses().unwrap();
    localStorage.setItem('classes', JSON.stringify(classes));
    return classes;
  }

  const getAllClassesAsync = async () => {
    let classes = localStorage.getItem('classes');
    if (classes)
      classes = JSON.parse(classes);
    else
      classes = await loadClasses();

    if (classes) {
      classes = ([
        {
          name: 'All Classes',
          id: -1,
          description: 'All Classes',
          isChecked: true
        },
        ...classes.map((item) => ({
          name: item.className,
          id: item.classID,
          description: item.className,
          isChecked: true
        }))
      ]);
      setClasses(classes);
    }
  };

  useEffect(() => {
    if(selectedClassIds != null && !selectedClassIds.includes(-1))
      getAllStudentsAsync(0, true, '');
  }, [selectedClassIds]);

  const getAllStudentsAsync = async (skip, isSearch, searchString) => {
    if(filterBy != 0 && selectedClassIds.length === 0) return;

    try {
      let options = {
        ids: removeAllSelected(selectedClassIds, classes),
        skip: skip,
        searchString: searchString
      };
      if (filterBy !== 0) { options = { ...options, take: takeSize }; }
      let response = await getStudents(options);
      if (response.error) {
        CustomToast(response.error.data.error, 'error');
      }
      else {
        setHasMore((skip + takeSize) < response.data.total);
        const mappedStudents = response.data.data.map(s => ({ id: s.studentId, name: `${s.lName} ${s.fName}` }));
        const updatedStudentsList = filterBy !== 0 && skip === 0 ? [{ id: -1, name: 'All Students' }, ...mappedStudents] : mappedStudents;
        setStudents(prev => isSearch ? updatedStudentsList : [...prev, ...updatedStudentsList]);

        if (filterBy === 0 && updatedStudentsList.length > 0) {
          setSelectedStudentIds([updatedStudentsList[0].id]);
          setStudentName(updatedStudentsList[0].name);
        } else if (filterBy !== 0) {
          setSelectedStudentIds([]);
          setStudentName('');
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getReportFilterData = async () => {
    await getAllCoursesAsync();
    await getAllClassesAsync();
    await getAllStudentsAsync(0, false, '');
  };

  const handleTabSwitch = (tabValue) => {
    setFilterBy(tabValue);
    setSelectedClassIds([]);
    setSelectedStudentIds([]);
    setStudents([]);

    if (tabValue == 0)
      getAllStudentsAsync(0, false, '');
    else
      getAllClassesAsync();
  };

  const handleMultiSelectChange = (selectedId, setState, dataList) => {
    setState((prev = []) => {
      const isSelected = prev.includes(selectedId);
      if (selectedId === -1) {
        return isSelected ? [] : dataList.map(item => item.id);
      }

      const cleanedPrev = prev.filter(id => id !== -1);
      if (isSelected) {
        return cleanedPrev.filter(id => id !== selectedId);
      } else {
        return [...cleanedPrev, selectedId];
      }
    });
  };

  const handleCourseSelectChange = (id) =>
    handleMultiSelectChange(id, setSelectedCourseIds, courses);

  const handleClassSelectChange = (id) =>
    handleMultiSelectChange(id, setSelectedClassIds, classes);

  const handleStudentSelectChange = (id) =>
    handleMultiSelectChange(id, setSelectedStudentIds, students);

  const removeAllSelected = (selectedIds, listedItems) => {
    if (!selectedIds?.includes(-1)) return selectedIds;
    const allOtherIds = listedItems.filter(item => item.id !== -1).map(item => item.id);
    const allSelected = allOtherIds.every(id => selectedIds.includes(id));
    return allSelected ? allOtherIds : selectedIds;
  };

  const validateReportSelections = () => {
    if (!selectedCourseIds.length) return 'course';
    if (filterBy === 1 && !selectedClassIds.length) return 'class';
    if (filterBy === 1 && selectedClassIds.length && !selectedClassIds.includes(-1) && !selectedStudentIds.length) return 'student';
    return null;
  };

  const handleReportPage = () => {
    const invalid = validateReportSelections();
    if (invalid) {
      CustomToast(`Please select a ${invalid} to create report.`, 'warning');
      return;
    }

    const cleanedCourseIds = removeAllSelected(selectedCourseIds, courses);
    const cleanedClassIds = removeAllSelected(selectedClassIds || [], classes);
    const cleanedStudentIds = selectedStudentIds.includes(-1) ? [] : selectedStudentIds;
    const courseHeader = selectedCourseIds.includes(-1) ? 'All Courses': courses.filter(item=> selectedCourseIds.includes(item.id)).map(item => item.name).join(', ');
    const classHeader = selectedClassIds?.includes(-1) ? 'All Classes' : classes.filter(item=> selectedClassIds?.includes(item.id)).map(item => item.name).join(', ');

    navigate('class-report',
      { state:
         { selectedCourseIds: cleanedCourseIds,
           selectedClassIds: cleanedClassIds,
           selectedStudentIds: cleanedStudentIds,
           filters: {courseHeader : courseHeader, classHeader: classHeader, studentIds: cleanedStudentIds, classIds: cleanedClassIds, courseIds: cleanedCourseIds}
         }
      });
  };

  const getPlaceHolderText = (label, list = []) => {
    if (list.length === 0) return `Select ${label}`;
    else if (list.includes(-1)) return 'All selected';
    else return `${list.length} item${list.length > 1 ? 's' : ''} selected`;
  };

  return (
    <>
      <div className='white-card min-h-500'>
        <div className='align-items-center d-flex justify-content-between mb-4'>
          <h1 className='mb-4'>Self Study Report</h1>
          <div>
            <button
              onClick={() => window.history.back()}
              className="btn btn-castleWhite report-btn">
              Back
            </button>
          </div>
        </div>
        <p>This report summarizes independent study sessions completed by students. It tracks progress across specific learning standards, highlighting a transition from initial areas of weakness to proficiency through targeted practice and the use of instructional hints and feedback.</p>
        <Row className='mt-4'>
          <Col className='col-4 px-2'>
            <label className='mb-2'>Course</label>
            {isCourseLoading?
              <SkeletonGrid
                rowCount={1}
                colCount={1}
                className={'p-3'} />
              :
              <DropDownMultiSelect
                value={courses}
                dropdownId='courseList'
                selectedIds={selectedCourseIds}
                onChange={(e) => handleCourseSelectChange(parseInt(e.key))}
                placeHolder={getPlaceHolderText('course', selectedCourseIds)}
                isPaginated={false}
              />
            }
          </Col>
          <Col className='col-4 px-2'>
            <Row>
              <Col>
                <label className='mb-2'>Filter By</label>
              </Col>
            </Row>
            <Row className="d-flex align-items-center blueTabs w-100 mx-0">
              <Col className={`justify-content-center btn ${filterBy == 0 ? 'active' : ''}`} onClick={() => handleTabSwitch(0)}>Student</Col>
              <Col className={`justify-content-center btn ${filterBy == 1 ? 'active' : ''}`} onClick={() => handleTabSwitch(1)}>Students in Class</Col>
            </Row>
          </Col>

          {filterBy == 1 &&
            <Col className='col-4 px-2'>
              <label className='mb-2'>Class</label>
              {isClassLoading ? 
                <SkeletonGrid
                  rowCount={1}
                  colCount={1}
                  className={'p-3'} />
                :
                <DropDownMultiSelect
                  value={classes}
                  dropdownId='classList'
                  selectedIds={selectedClassIds}
                  onChange={(e) => handleClassSelectChange(parseInt(e.key))}
                  placeHolder={getPlaceHolderText('class', selectedClassIds)}
                  isPaginated={false}
                />
              }
            </Col>
          }

          {(filterBy === 0 || (filterBy === 1 && selectedClassIds.length > 0 && !selectedClassIds.includes(-1))) &&
            <Col className={`col-4 px-2 ${filterBy == 1 && !selectedClassIds.includes(-1) ? 'mt-4' : ''}`}>
              <label className='mb-2'>Student</label>
              {isStudentLoading || isStudentFetching ?
                <SkeletonGrid
                  rowCount={1}
                  colCount={1}
                  className={'p-3'} />
                :
                (filterBy == 0 ?
                  <DropDownSelect
                    value={students}
                    selectedId={selectedStudentIds[0]}
                    setSelectedId={(id) => setSelectedStudentIds([id])}
                    dropdownId="studentList"
                    selectedName={studentName}
                    setSelectedName={setStudentName}
                  />
                  :
                  <DropDownMultiSelect
                    value={students}
                    dropdownId='studentList'
                    selectedIds={selectedStudentIds}
                    onChange={(e) => handleStudentSelectChange(parseInt(e.key))}
                    placeHolder={getPlaceHolderText('student', selectedStudentIds)}
                    fetch={getAllStudentsAsync}
                    hasMore={hasMore}
                    takeSize={takeSize}
                    isPaginated={true}
                  />
                )
              }
            </Col>
          }
        </Row>
        <div className='w-100 d-flex gap-3 mt-4'>
          <div className='w-100'></div>
          <button className="w-10 btn btn-castleRed" onClick={() => handleReportPage()}>
            Create Report
          </button>
        </div>
      </div>
    </>
  );
};

export default SelfStudyFilter;